import React, { useContext, useEffect } from 'react';
import { observer } from "mobx-react-lite";
import { RootStoreContext } from '../../stores/rootStore';
import { Link } from 'react-router-dom';
import { saveVisit } from '../../api/visitTrackerService';

const Home: React.FC = observer(() => {
    const rootStore = useContext(RootStoreContext);
    const { loading } = rootStore;
    const bundle1 = rootStore.bundle1;
    const bundle2 = rootStore.bundle2;
    const bundle5 = rootStore.bundle5;
    const bundleCustom = rootStore.bundleCustom;
    const { Qnt, increaseQnt, decreaseQnt } = rootStore.bundleCustom;
    const curr = rootStore.currency === 'EUR' ? '€' : '£';

    //billing cycle for every card; only offered when the backend advertises monthly pricing
    const monthlyAvailable = rootStore.monthlyPricingAvailable;
    const cycle = bundle1.billingPeriod;
    const isMonthly = cycle === 'monthly';
    const billedLabel = isMonthly ? 'billed monthly' : 'billed yearly';
    //"Save NN%" on Yearly vs paying month-by-month, from the single-seat rates
    const annual1 = rootStore.priceList[1];
    const monthly1 = rootStore.monthlyPriceList[1];
    const savePct = monthlyAvailable && annual1 > 0 && monthly1 > 0
        ? Math.round((1 - annual1 / (monthly1 * 12)) * 100)
        : 0;
    const cycleParam = monthlyAvailable ? `?cycle=${cycle}` : '';

    const handleQntInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        rootStore.bundleCustom.setQnt(Number(e.target.value));
    };

    useEffect(() => {
        saveVisit('home');
    }, []);

    if (loading) {
        return <div className="page-center"><p>Loading…</p></div>;
    }

    return (
        <div className="pricing-wrap">
            <div className="pricing-header">
                <p className="pricing-eyebrow">Pro subscription</p>
                <h1>Choose your plan</h1>
                {monthlyAvailable ? (
                    <p><strong>Desktop Call Recorder</strong> is licensed per computer. Pick the number of licences you need — pay monthly for flexibility or yearly to save.</p>
                ) : (
                    <p><strong>Desktop Call Recorder</strong> is licensed per computer. Plans are billed once a year.</p>
                )}
                {monthlyAvailable && (
                    <div className="billing-switch" role="tablist" aria-label="Billing period">
                        <button
                            type="button"
                            className={`bs-opt${isMonthly ? ' active' : ''}`}
                            role="tab"
                            aria-selected={isMonthly}
                            onClick={() => rootStore.setPricingBillingPeriod('monthly')}
                        >Monthly</button>
                        <button
                            type="button"
                            className={`bs-opt${!isMonthly ? ' active' : ''}`}
                            role="tab"
                            aria-selected={!isMonthly}
                            onClick={() => rootStore.setPricingBillingPeriod('annual')}
                        >Yearly{savePct > 0 && <span className="bs-save">Save {savePct}%</span>}</button>
                    </div>
                )}
            </div>

            <div className="tier-grid">
                <div className="tier">
                    <div className="tier-head">
                        <span className="tier-name">Single Computer</span>
                        <span className="tier-icon">
                            <svg viewBox="0 0 24 18" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="1" y="1" width="22" height="14" rx="1.5"/><path d="M9 17h6M12 15v2"/></svg>
                        </span>
                    </div>
                    <div className="price">
                        <span className="price-cur">{curr}</span>
                        <span className="price-num">{bundle1.PricePerMonthString}</span>
                        <span className="price-unit">per computer<br />per month</span>
                    </div>
                    <span className="price-year">{curr} {bundle1.BundlePriceString} {billedLabel}</span>
                    <p className="tier-desc">For use on a single computer</p>
                    {bundle1.USDPriceString && bundle1.EURPriceString && (
                        <p className="tier-conv">≈ ${bundle1.USDPriceString} or €{bundle1.EURPriceString}</p>
                    )}
                    <Link to={`/purchase/${bundle1.Qnt}${cycleParam}`} className="tier-cta">Purchase</Link>
                </div>

                <div className="tier featured">
                    <div className="tier-head">
                        <span className="tier-name">Twin Pack</span>
                        <span className="tier-icon">
                            <svg viewBox="0 0 24 18" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="1" y="1" width="14" height="10" rx="1.5"/><rect x="9" y="6" width="14" height="10" rx="1.5" fill="#fff"/></svg>
                        </span>
                    </div>
                    <div className="price">
                        <span className="price-cur">{curr}</span>
                        <span className="price-num">{bundle2.PricePerMonthString}</span>
                        <span className="price-unit">per computer<br />per month</span>
                    </div>
                    <span className="price-year">{curr} {bundle2.BundlePriceString} {billedLabel}</span>
                    <p className="tier-desc">2 computers</p>
                    {bundle2.USDPriceString && bundle2.EURPriceString && (
                        <p className="tier-conv">≈ ${bundle2.USDPriceString} or €{bundle2.EURPriceString}</p>
                    )}
                    <Link to={`/purchase/${bundle2.Qnt}${cycleParam}`} className="tier-cta">Purchase</Link>
                </div>

                <div className="tier">
                    <div className="tier-head">
                        <span className="tier-name">Small Office</span>
                        <span className="tier-icon">
                            <svg viewBox="0 0 24 18" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="1" y="1" width="22" height="14" rx="1.5"/><path d="M9 17h6M12 15v2"/></svg>
                            <span className="x">×{bundle5.Qnt}</span>
                        </span>
                    </div>
                    <div className="price">
                        <span className="price-cur">{curr}</span>
                        <span className="price-num">{bundle5.PricePerMonthString}</span>
                        <span className="price-unit">per computer<br />per month</span>
                    </div>
                    <span className="price-year">{curr} {bundle5.BundlePriceString} {billedLabel}</span>
                    <p className="tier-desc">Up to {bundle5.Qnt} computers</p>
                    {bundle5.USDPriceString && bundle5.EURPriceString && (
                        <p className="tier-conv">≈ ${bundle5.USDPriceString} or €{bundle5.EURPriceString}</p>
                    )}
                    <Link to={`/purchase/${bundle5.Qnt}${cycleParam}`} className="tier-cta">Purchase</Link>
                </div>

                <div className="tier">
                    <div className="tier-head">
                        <span className="tier-name">Custom</span>
                        <span className="tier-icon">
                            <svg viewBox="0 0 24 18" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="1" y="1" width="22" height="14" rx="1.5"/><path d="M9 17h6M12 15v2"/></svg>
                            <span className="x">×{Qnt}</span>
                        </span>
                    </div>
                    <div className="price">
                        <span className="price-cur">{curr}</span>
                        <span className="price-num">{bundleCustom.PricePerMonthString}</span>
                        <span className="price-unit">per computer<br />per month</span>
                    </div>
                    <span className="price-year">{curr} {bundleCustom.BundlePriceString} {billedLabel}</span>
                    <p className="tier-desc">Choose the number of licenses</p>
                    <div className="tier-qty">
                        <button type="button" onClick={decreaseQnt}>−</button>
                        <input
                            type="number"
                            value={Qnt}
                            onChange={handleQntInputChange}
                            name="custom-input-number"
                        />
                        <button type="button" onClick={increaseQnt}>+</button>
                    </div>
                    {bundleCustom.USDPriceString && bundleCustom.EURPriceString && (
                        <p className="tier-conv">≈ ${bundleCustom.USDPriceString} or €{bundleCustom.EURPriceString}</p>
                    )}
                    <Link to={`/purchase/${Qnt}${cycleParam}`} className="tier-cta">Purchase</Link>
                </div>
            </div>

            <div className="faq-strip">
                <div className="faq-item">
                    <h3>What is a computer license?</h3>
                    <p>Each license allows Desktop Call Recorder to run on one computer. A Twin Pack covers two computers, and so on.</p>
                </div>
                <div className="faq-item">
                    <h3>How does billing work?</h3>
                    {monthlyAvailable ? (
                        <p>Monthly plans renew each month, yearly plans once a year — billed in {rootStore.currency}. You get a receipt for every payment and a notice ahead of each yearly renewal. Cancel whenever you like: Pro stays active until the end of the paid period.</p>
                    ) : (
                        <p>Plans renew once a year — billed in {rootStore.currency}. You get a receipt for every payment and a notice ahead of each renewal. Cancel whenever you like: Pro stays active until the end of the paid period, and if auto-renewal is off the recorder app and our emails remind you before it expires.</p>
                    )}
                </div>
                <div className="faq-item">
                    <h3>Can I upgrade later?</h3>
                    <p>Yes — you can purchase additional licenses at any time. Each subscription renews independently.</p>
                </div>
            </div>
        </div>
    );
});

export default Home;
